import type {
  QuickBooksFpaStatementRender,
  QuickBooksFpaStatementRow,
} from '../../data/ServerApiClient'

const cleanText = (value: any): string => String(value ?? '').trim()

const normalizedTotalComparisonLabel = (value: any): string => (
  cleanText(value)
    .replace(/^total\s+/i, '')
    .replace(/\s+accounts$/i, '')
    .replace(/\s+/g, ' ')
    .replace(/^income$/i, 'revenue')
    .replace(/^cost of goods sold$/i, 'cost of sales')
    .replace(/expenses$/i, 'expense')
    .toLowerCase()
)

const isAccountTotalLabel = (value: any): boolean => /\baccounts$/i.test(
  cleanText(value).replace(/^total\s+/i, ''),
)

const statementRowLabelsMatch = (
  left: QuickBooksFpaStatementRow | null | undefined,
  right: QuickBooksFpaStatementRow | null | undefined,
): boolean => (
  normalizedTotalComparisonLabel(left?.label) === normalizedTotalComparisonLabel(right?.label)
)

const statementRowValuesMatch = (
  left: QuickBooksFpaStatementRow | null | undefined,
  right: QuickBooksFpaStatementRow | null | undefined,
): boolean => {
  const leftValues = Array.isArray(left?.values) ? left?.values || [] : []
  const rightValues = Array.isArray(right?.values) ? right?.values || [] : []
  const width = Math.max(leftValues.length, rightValues.length)
  if (!width) return false
  for (let index = 0; index < width; index += 1) {
    const leftValue = Number(leftValues[index] ?? 0)
    const rightValue = Number(rightValues[index] ?? 0)
    if (Math.abs(leftValue - rightValue) > 0.005) return false
  }
  return true
}

const removeRedundantCollapsedTotals = (rows: QuickBooksFpaStatementRow[]): QuickBooksFpaStatementRow[] => {
  const out: QuickBooksFpaStatementRow[] = []
  for (const row of rows) {
    const previous = out[out.length - 1]
    if (
      previous
      && row.is_total
      && statementRowLabelsMatch(row, previous)
      && statementRowValuesMatch(row, previous)
    ) {
      if (previous.is_total && isAccountTotalLabel(previous.label) && !isAccountTotalLabel(row.label)) {
        out[out.length - 1] = row
      }
      continue
    }
    out.push(row)
  }
  return out
}

const blankParentValuesDuplicatedByVisibleTotals = (rows: QuickBooksFpaStatementRow[]): QuickBooksFpaStatementRow[] => (
  rows.map((row, index) => {
    if (row.is_total) return row
    const level = Math.max(0, Number(row.level) || 0)
    let hasVisibleChild = false
    for (let cursor = index + 1; cursor < rows.length; cursor += 1) {
      const candidate = rows[cursor]
      const candidateLevel = Math.max(0, Number(candidate.level) || 0)
      if (
        candidate.is_total
        && statementRowLabelsMatch(row, candidate)
        && statementRowValuesMatch(row, candidate)
      ) {
        return hasVisibleChild
          ? { ...row, values: (Array.isArray(row.values) ? row.values : []).map(() => null) }
          : row
      }
      if (!candidate.is_total && candidateLevel <= level) break
      if (candidateLevel > level) hasVisibleChild = true
    }
    return row
  })
)

export const visiblePresentationStatementRows = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  limit = 18,
  options: { collapseGroups?: boolean } = {},
): QuickBooksFpaStatementRow[] => {
  const rows = Array.isArray(statement?.rows) ? statement.rows : []
  const meaningful = rows.filter((row) => {
    if (!row) return false
    const label = cleanText(row.label)
    if (!label) return false
    if (row.row_type === 'spacer') return false
    const values = Array.isArray(row.values) ? row.values : []
    return row.is_total || values.some((value) => Math.abs(Number(value) || 0) > 0.000001)
  })
  if (!options.collapseGroups) return meaningful.slice(0, limit)
  const collapsed = meaningful.filter((row) => {
    const rowType = cleanText(row.row_type).toLowerCase()
    const level = Math.max(0, Number(row.level) || 0)
    return row.is_total || row.is_formula || rowType === 'group' || rowType === 'heading' || level === 0
  })
  const deduped = removeRedundantCollapsedTotals(collapsed.length ? collapsed : meaningful)
  return blankParentValuesDuplicatedByVisibleTotals(deduped).slice(0, limit)
}
